import React, {
  useState,
  useEffect,
  Fragment,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { Button, message } from 'antd';
import moment, { Moment } from 'moment';
import { isEmpty } from 'underscore';
import LineChart from '../../../../components/Chart/LineChart';
import { ChartBox } from '../ChartBox';
import { IPlotVariable } from '../../../../typings/analytics';
import { DateFormat, DT0Format } from '../../../../generic/StaticVariable';
import { Select, DatePicker } from 'antd';
import { Label } from '../../../../components/Label/Label';
import styles from '../../Style/AnalyticsPanel.module.scss';
import { IBestDataset } from '../../../../typings/analytics';
import { IGetBasisChartV2ApiPayload } from '../../../../typings/chart';
import { formatDate } from '../../../../generic/common';
import { StoreContext } from '../../../../store/store';
import { getPointNameAndAcronym } from '../AnalyticsPanel';
import { SplitCoordinates } from '../../../../generic/coordinate';
import { IAnalytcisUserInput } from '../../../../typings/IAnalyticsUserInput';
import { useData } from '../../Store/DatasetContext';
import { DATASET_CATEGORIES } from '../../../../generic/filterBestDataset';

export default function TSPlot(props: {
  onDatasetChange: (item: string) => void
  datasets: IBestDataset[];
  variables: IPlotVariable[];
  defaultValueShortName: string;
  dateRange: string[];
  previewDatasetId: string;
  previewDatasetTimeRange: [moment.Moment, moment.Moment];
  tsData: { [key: string]: any }[];
  onUpdate: (values: IGetBasisChartV2ApiPayload) => void;
  updatedTSData: any[];
  pointProps: string;
  pointName: string;
  userInput: IAnalytcisUserInput;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  loadingButtonUpdate: boolean
}) {
  const {
    datasetBriefs,
    markerCollections,
  } = useContext(StoreContext);

  const point = markerCollections.find((x) => x.active);

  const {
    tsData,
    datasets,
    onUpdate,
    dateRange,
    variables,
    defaultValueShortName,
    updatedTSData,
    previewDatasetId,
    previewDatasetTimeRange,
    pointProps,
    pointName,
    userInput,
    loadingButtonUpdate,
    updateUserInput,
    onDatasetChange
  } = props;

  const [tsPlotData, setTSPlotData] = useState<any[]>([]);
  const [dataset, setDataset] = useState(userInput.TimeSeriesPlot.dataset);
  const [variable, setVariable] = useState<IPlotVariable | null>(
    userInput.TimeSeriesPlot.variable
  );
  const [name, setName] = useState('');
  const [downloadName, setDownloadName] = useState<string | undefined>();
  const [startDate, setStartDate] = useState(
    userInput.TimeSeriesPlot.startDate
      ? userInput.TimeSeriesPlot.startDate
      : moment(dateRange[1]).subtract(1, 'years')
  );
  const [endDate, setEndDate] = useState(
    userInput.TimeSeriesPlot.endDate
      ? userInput.TimeSeriesPlot.endDate
      : moment(dateRange[1])
  );
  const [updating, setUpdating] = useState(false);
  const [previewsettingChanged, setPreviewsettingChanged] = useState(false);

  const chartRef = useRef<LineChart>(null);

  const handleExportImage = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.exportChartAsImage();
    }
  }, []);

  useEffect(() => {
    updateUserInput({
      TimeSeriesPlot: {
        dataset: dataset,
        variable: variable,
        startDate: startDate,
        endDate: endDate,
      },
    });
  }, [dataset, variable, startDate, endDate]);

  const onSelectDataset = (id: string) => {
    onDatasetChange(id)
  }

  const doVariableChange = (active: number) => {
    const item = props.variables.find((a) => a.Index === active)!;
    setPreviewsettingChanged(true);
    setVariable(item);
  };

  const doStartDateChange = (value: any) => {
    if (value) {
      setPreviewsettingChanged(true);
      setStartDate(value);
    }
  };

  const doEndDateChange = (value: any) => {
    if (value) {
      setPreviewsettingChanged(true);
      setEndDate(value);
    }
  };

  useEffect(() => {
    if (!isEmpty(datasets)) {
      setDataset(previewDatasetId);
    }
  }, [datasets, previewDatasetId]);

  useEffect(() => {
    if (variable !== undefined && updatedTSData.length > 0) {
      const dataKey = variable?.Name.split(' ').join('_') || '';
      const firstTSData =
        updatedTSData && updatedTSData[0] ? updatedTSData[0] : null;
      if (variable && firstTSData && dataKey in firstTSData) {
        setUpdating(false);
        setName(`${variable.Name} [[${variable.Unit}]]`);
        setTSPlotData(
          updatedTSData.map((ts) => {
            return {
              Time: moment(ts.Time, 'YYYY-MM-DD HH:mm:ss').toDate(),
              Value: ts[dataKey],
            };
          })
        );
      }
    }
  }, [updatedTSData, variable]);


  useEffect(() => {
    previewsettingChanged &&
      message.warn('Please click Update once you finished your selection');
  }, [previewsettingChanged]);

  useEffect(() => {
    if (tsData && !isEmpty(variables) && !isEmpty(defaultValueShortName)) {
      const defaultVariable = variables.find(
        (v: IPlotVariable) => v.ShortName === defaultValueShortName
      );
      const selectedVariable = defaultVariable ? defaultVariable : variables[0]
      setVariable(selectedVariable)
      const dataKey = selectedVariable.Name.split(' ').join('_');
      const firstTSData = tsData && tsData[0] ? tsData[0] : null;
      if (firstTSData && dataKey in firstTSData) {
        setName(`${selectedVariable.Name} [[${selectedVariable.Unit}]]`);
        setTSPlotData(
          tsData.map((ts) => {
            return {
              Time: moment(ts.Time, 'YYYY-MM-DD HH:mm:ss').toDate(),
              Value: ts[dataKey],
            };
          })
        );
      }
    }
  }, [variables, tsData, defaultValueShortName]);

  useEffect(() => {
    if (!previewsettingChanged && dateRange && dateRange.length > 1) {
      setDownloadName(
        getPointNameAndAcronym(previewDatasetId, datasetBriefs, point) +
        pointProps +
        '_' +
        formatDate(moment(dateRange[0])) +
        '_' +
        formatDate(moment(dateRange[1])) +
        '_Time_Series_Plot'
      );
    }
  }, [
    dateRange,
    previewsettingChanged,
    pointProps,
    previewDatasetId,
    datasetBriefs,
    point && point.label,
  ]);

  const onUpdateTSPlot = useCallback(() => {
    if (variable && dataset) {
      setUpdating(true);
      setDownloadName(
        getPointNameAndAcronym(dataset, datasetBriefs, point) +
        pointProps +
        '_' +
        formatDate(startDate) +
        '_' +
        formatDate(endDate) +
        '_Time_Series_Plot'
      );
      const values: IGetBasisChartV2ApiPayload = {
        datasetName: dataset,
        parameters: variable.Name,
        startDate: startDate.format(DT0Format),
        endDate: endDate.format(DT0Format),
      };
      onUpdate(values);
    }
  }, [
    startDate,
    endDate,
    dataset,
    variable,
    pointProps,
    datasetBriefs,
    point && point.label,
    onUpdate,
  ]);

  function disabledStartDate(current: Moment) {
    // Can not select date after end date
    const isAfterEndDate = current && current > endDate;
    // and not before preview dataset's time range and not after preview dataset's time range
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(previewDatasetTimeRange[0], 'day');
  }

  function disabledEndDate(current: Moment) {
    // Can not select date before start date and not after preview dataset's time range
    const isBeforeStartDate = current && current < startDate;
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(previewDatasetTimeRange[1], 'day');
  }

  const memoCoords = useMemo(() => {
    return SplitCoordinates(pointProps);
  }, [pointProps]);

  const categories = useMemo(() => {
    const label = datasets.map((bd: IBestDataset) => {
      return bd.label;
    })

    return Array.from(new Set(label));
  }, [datasets])

  const actionRows = (
    <Fragment>
      <div className={styles.actionRow}>
        <Label label="Dataset" className={styles.flex1}>
          <Select
            value={dataset}
            size="small"
            style={{ width: '100%' }}
            onChange={onSelectDataset}
            disabled={loadingButtonUpdate}
          >
            {categories.map((category: string) => {
              const section = category === 'SW'
                ? DATASET_CATEGORIES.WAVES
                : category === 'HD'
                  ? DATASET_CATEGORIES.OCEAN
                  : DATASET_CATEGORIES.ATMOSPHERE;
              return (
                <Select.OptGroup key={category} label={section}>
                  {datasets
                    .filter((item) => {
                      return item.label === category;
                    })
                    .map((item) => {
                      return (
                        <Select.Option key={item.id} value={item.id}>
                          {item.value}
                        </Select.Option>
                      );
                    })}
                </Select.OptGroup>
              );
            })}
          </Select>
        </Label>
      </div>
      <div className={styles.actionRow}>
        <Label label="Variable" className={styles.actionItem}>
          <Select
            value={variable?.Index ? variable.Index : 0}
            size="small"
            style={{ width: '100%' }}
            onChange={doVariableChange}
          >
            {props.variables
              .filter((item) => item.Name !== 'Time')
              .map((item) => {
                return (
                  <Select.Option key={item.Index} value={item.Index}>
                    {item.Name}
                  </Select.Option>
                );
              })}
          </Select>
        </Label>
        <Label label="Start Date" className={styles.actionItem}>
          <DatePicker
            size="small"
            value={moment(startDate)}
            disabledDate={disabledStartDate}
            onChange={doStartDateChange}
            format={DateFormat}
          />
        </Label>
        <Label label="End Date" className={styles.actionItem}>
          <DatePicker
            size="small"
            value={moment(endDate)}
            disabledDate={disabledEndDate}
            onChange={doEndDateChange}
            format={DateFormat}
          />
        </Label>
      </div>
      <div className={styles.analyticsSubHeadlineContainer}>
        <div className={styles.analyticsPointName}>
          {pointName}{' '}
          <span className={styles.analyticsCoords}>
            ({memoCoords.lng}°E; {memoCoords.lat}°N)
          </span>
        </div>
        <Button type='link' className={styles.buttonLink} onClick={handleExportImage} disabled={updating || loadingButtonUpdate}>
          Download as JPG
        </Button>
        <Button
          className={styles.analyticsButton}
          type="primary"
          onClick={onUpdateTSPlot}
          loading={(updating || loadingButtonUpdate)}
        >
          {'Update'}
        </Button>
      </div>
    </Fragment>
  );

  return (
    <ChartBox
      actionRow={actionRows}
      key={'Time Series Plot'}
      title={'Time Series Plot'}
      height={320}
      plot={
        <LineChart name={name} data={tsPlotData} ref={chartRef} downloadName={downloadName} />
      }
      dataset={props.datasets.find((d) => d.id === dataset)?.label || ''}
    ></ChartBox>
  );
}
